import { Formik } from "formik";
import difference from "lodash/difference";
import includes from "lodash/includes";
import pick from "lodash/pick";
import React, { Component, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";

import { fetchLegacyInvitation } from "../../../../actions/employers/organizationActions";
import { createRecruiter } from "../../../../actions/recruiters/individualActions";
import LoadingPage from "../../../General/LoadingPage";
import PageCard from "../../../General/PageCard";
import PageCardRowHeader from "../../../General/PageCardRowHeader";
import TermsList from "../../../recruiter/Activation/TermsList";
import AcceptedInvitation from "../AcceptedInvitation";
import EmployerLogo from "../FormContent/EmployerLogo";
import {
  buildValidationSchema,
  bundleFormContext,
} from "../FormContent/formHelpers";
import NotFoundContent from "../FormContent/NotFoundContent";
import {
  FieldSection,
  EmailField,
  PasswordField,
  AgreesToTermsField,
  AgreesToTempTermsField,
} from "../FormContent/RegistrationFormFields";
import RegistrationFormFooter from "../FormContent/RegistrationFormFooter";
import RFIFeaturesGraphic from "../FormContent/RFIFeaturesGraphic";

function LegacyGreeting({
  name,
  employerOrganization,
  tempEnabled,
  permEnabled,
}) {
  function renderPlacementTypes() {
    if (permEnabled && tempEnabled) {
      return (
        <>
          <span style={{ fontWeight: "bold" }}>permanent placement</span>
          {" and "}
          <span style={{ fontWeight: "bold" }}>contingency worker</span>
        </>
      );
    }

    if (tempEnabled) {
      return <span style={{ fontWeight: "bold" }}>contingency worker</span>;
    }

    return <span style={{ fontWeight: "bold" }}>permanent placement</span>;
  }

  return (
    <div style={{ margin: "30px 20px 10px 20px", width: "90%" }}>
      <div style={{ marginBottom: "20px" }}>
        <h5>
          {"Hi "}
          {name},
        </h5>
      </div>
      {"Welcome to "}
      <span style={{ fontWeight: "bold" }}>
        {employerOrganization}
        ’s
      </span>{" "}
      RecruitiFi agency portal. As a Preferred Agency, you will receive{" "}
      {renderPlacementTypes()}
      {" roles from "}
      {employerOrganization}
      .
      <br />
      <br />
      Please create your account to continue your agency relationship and
      receive open jobs.
    </div>
  );
}

function Form({ legacyAgency, preferredLink, history }) {
  const dispatch = useDispatch();
  const { tempEnabled, permEnabled } = preferredLink;

  const fields = useMemo(() => {
    const standardFields = ["email", "password", "legacy_invitation_id"];

    if (permEnabled) {
      standardFields.push("agrees_to_terms");
    }

    if (tempEnabled) {
      standardFields.push("agrees_to_temp_terms");
    }

    return standardFields;
  }, [permEnabled, tempEnabled]);

  const initialValues = useMemo(
    () =>
      pick(
        {
          agrees_to_terms: false,
          agrees_to_temp_terms: false,
          email: legacyAgency.email,
          legacy_invitation_id: legacyAgency.id,
          password: "",
        },
        fields
      ),
    [legacyAgency.email, legacyAgency.id, fields]
  );

  const validationSchema = useMemo(
    () =>
      buildValidationSchema(
        difference(fields, ["email", "legacy_invitation_id"])
      ),
    [fields]
  );

  const onSubmit = useCallback(
    (values, { setSubmitting }) =>
      dispatch(createRecruiter({ ...values, stripe_agrees_to_terms: true }))
        .then(() => {
          setSubmitting(false);
          history.push("/activation");
        })
        .catch(() => {
          setSubmitting(false);
        }),
    [dispatch, history]
  );

  return (
    <Formik {...{ initialValues, validationSchema, onSubmit }}>
      {(formikBag) => (
        <PageCard
          title="Agency Registration"
          withImageCardHeader
          avatar={<EmployerLogo logoURL={legacyAgency.organizationAvatarUrl} />}
          contentPadding={24}
          footerVisible
          footer={<RegistrationFormFooter {...bundleFormContext(formikBag)} />}
        >
          <RFIFeaturesGraphic />
          <LegacyGreeting {...legacyAgency} {...preferredLink} />
          <PageCardRowHeader
            title="Your Terms Overview"
            lineStyle={{ width: "100%" }}
          />
          <TermsList preferredLink={preferredLink} />
          <FieldSection>
            <EmailField disabled {...bundleFormContext(formikBag)} />
            <PasswordField {...bundleFormContext(formikBag)} />
            {includes(fields, "agrees_to_terms") && (
              <AgreesToTermsField {...bundleFormContext(formikBag)} />
            )}
            {includes(fields, "agrees_to_temp_terms") && (
              <AgreesToTempTermsField {...bundleFormContext(formikBag)} />
            )}
          </FieldSection>
        </PageCard>
      )}
    </Formik>
  );
}

const FormWithRouter = withRouter(Form);

class LegacyRegistrationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      legacyAgency: {},
      preferredLink: {},
      loadError: false,
    };
  }

  componentDidMount() {
    const { legacyInvitationToken } = this.props;

    fetchLegacyInvitation(legacyInvitationToken)
      .then(({ legacyInvitation, preferredLink }) => {
        this.setState({
          legacyAgency: legacyInvitation,
          preferredLink,
          loaded: true,
        });
      })
      .catch(() => {
        this.setState({
          loadError: true,
          loaded: true,
        });
      });
  }

  renderContent() {
    const { legacyAgency, preferredLink, loadError } = this.state;

    if (!(loadError || legacyAgency.acceptedAt)) {
      return <FormWithRouter {...{ preferredLink, legacyAgency }} />;
    }

    return (
      <PageCard
        title="Agency Registration"
        withImageCardHeader
        avatar={<EmployerLogo logoURL={legacyAgency.organizationAvatarUrl} />}
        contentPadding={24}
        footerVisible={false}
      >
        {legacyAgency.acceptedAt ? (
          <AcceptedInvitation />
        ) : (
          <NotFoundContent title="Agency invite not found" />
        )}
      </PageCard>
    );
  }

  render() {
    const { loaded } = this.state;

    return loaded ? this.renderContent() : <LoadingPage />;
  }
}

export default LegacyRegistrationForm;
